'use strict';
const oneTimePasswordImpl = require('./one-time-password'),
	googleMetadataToFileContext = require('./google-metadata-to-file-context'),
	withoutExtension = require('./without-extension');
module.exports = function GoogleFileEditingSession(googleDriveService, collaborationModel, formatConverter, unsavedChangesModel, thumbnailBuilder, optional) {
	if (!googleDriveService || !collaborationModel || !formatConverter || !unsavedChangesModel || !thumbnailBuilder) {
		throw new Error('invalid-args');
	}
	let currentFileContext;
	const self = this,
		fileMimeType  = 'application/vnd.mindmup',
		canImport = function (googleFileMeta) {
			return googleFileMeta.fileExtension === 'mm' || googleFileMeta.mimeType === 'application/x-freemind' ||  googleFileMeta.mimeType === 'application/vnd-freemind';
		},
		canLoad = function (googleFileMeta) {
			return googleFileMeta.fileExtension === 'mup' || googleFileMeta.mimeType === fileMimeType;
		},
		isRealtime = function (googleFileMeta) {
			return googleMetadataToFileContext(googleFileMeta).isRealtime;
		},
		oneTimePassword = (optional && optional.oneTimePassword) || oneTimePasswordImpl,
		activityLog = optional && optional.activityLog,
		sessionId = oneTimePassword(),
		saveToNewFile = function (oldFileMeta, newName) {
			return thumbnailBuilder()
			.then(thumbnailUrl => googleDriveService.replaceFileContents(
				undefined,
				JSON.stringify(collaborationModel.getActiveContent(), null, 2),
				{
					mimeType: fileMimeType,
					name: newName || oldFileMeta.name,
					parents: oldFileMeta.parents
				},
				thumbnailUrl
			))
			.then((newFileMeta) => currentFileContext = googleMetadataToFileContext(newFileMeta));
		},
		processFileLoad = function (fileMeta, pendingChanges) {
			if (!canLoad(fileMeta)) {
				throw 'invalidFileType';
			}
			return googleDriveService.fetchFileContents(fileMeta.id)
				.then(formatConverter.fromMup)
				.then(content => collaborationModel.initializeSession(sessionId, content, pendingChanges || []))
				.then(() => {
					if (fileMeta.mimeType === fileMimeType) {
						return fileMeta;
					} else {
						return thumbnailBuilder()
						.then(thumbnailUrl => googleDriveService.replaceFileContents(
							fileMeta.id,
							JSON.stringify(collaborationModel.getActiveContent(), null, 2),
							{mimeType: fileMimeType},
							thumbnailUrl
						));
					}
				})
				.then((newFileMeta) => currentFileContext = googleMetadataToFileContext(newFileMeta));
		},
		processFileImport = function (fileMeta) {
			if (!canImport(fileMeta)) {
				throw 'invalidFileType';
			}
			return googleDriveService.fetchFileContents(fileMeta.id)
				.then(formatConverter.fromMM)
				.then(content => collaborationModel.initializeSession(sessionId, content, []))
				.then(() => saveToNewFile(fileMeta, withoutExtension(fileMeta.name)));
		},
		checkForRemoteChanges = function () {
			if (!currentFileContext) {
				return Promise.resolve(false);
			}
			return googleDriveService.getMetaData(currentFileContext.fileId)
				.then(meta => {
					if (!meta) {
						throw 'network-error';
					}
					const newContext = googleMetadataToFileContext(meta);
					return (newContext.checksum !== currentFileContext.checksum);
				});
		};
	self.loadFile = function (fileId) {
		if (activityLog) {
			activityLog.log('google-file-editing-session', 'loadFile:request', fileId);
		}
		return googleDriveService.getMetaData(fileId)
			.then(fileMeta => {
				if (activityLog) {
					activityLog.log('google-file-editing-session', 'loadFile:file-meta', JSON.stringify(fileMeta));
				}
				if (canLoad(fileMeta)) {
					return processFileLoad(fileMeta);
				} else if (isRealtime(fileMeta)) {
					throw 'realtime-unavailable';
				} else if (canImport(fileMeta)) {
					return processFileImport(fileMeta);
				} else {
					throw 'invalidFileType';
				}
			})
			.then(fileContext => {
				unsavedChangesModel.start({fileId: fileContext.fileId});
				return fileContext;
			});
	};
	self.getFileContext = function () {
		if (!currentFileContext) {
			return Promise.resolve();
		}
		return googleDriveService.getMetaData(currentFileContext.fileId)
		.then(fileMeta => googleMetadataToFileContext(fileMeta))
		.then(context => {
			context.remoteChanged = !!(context && currentFileContext && context.checksum !== currentFileContext.checksum);
			return context;
		});
	};
	self.mergeFile = function () {
		if (!currentFileContext) {
			return Promise.reject('no-file-loaded');
		}
		return googleDriveService.getMetaData(currentFileContext.fileId)
			.then((fileMeta) => processFileLoad(fileMeta, unsavedChangesModel.getUnsavedChanges()));
	};
	self.saveNewVersion = function (options) {
		if (!currentFileContext) {
			throw new Error('invalid-args'); // probably needs to change to support imports and new files!
		}
		return Promise.resolve()
		.then(() => {
			if (options && options.overwrite) {
				return false;
			}
			return checkForRemoteChanges();
		})
		.then(hasRemoteChanges => {
			if (!hasRemoteChanges) {
				return;
			}
			throw 'remote-changed';
		})
		.then(thumbnailBuilder)
		.then((thumbnailUrl) => {
			unsavedChangesModel.notifyFileSaving();
			return googleDriveService.replaceFileContents(
				currentFileContext.fileId,
				JSON.stringify(collaborationModel.getActiveContent(), null, 2),
				{mimeType: fileMimeType},
				thumbnailUrl,
				options && options.progress
			);
		}).then(result => {
			unsavedChangesModel.notifyFileSaved();
			return currentFileContext = googleMetadataToFileContext(result);
		});
	};

	self.patchAppProperties = function (properties) {
		if (!currentFileContext) {
			return Promise.reject('no-file-loaded');
		}
		return googleDriveService.patchAppProperties(currentFileContext.fileId, properties)
			.then(result => googleMetadataToFileContext(result));
	};
	collaborationModel.addEventListener('localContentEvent', unsavedChangesModel.localContentEvent);
};
